// growthStageService.js

import { api } from './config';

/**
 * Servicio para gestionar el análisis de etapas de crecimiento
 */
const growthStageService = {
  /**
   * Analizar y determinar la etapa de crecimiento actual
   */
  analyzeGrowthStage: async () => {
    try {
      const response = await api.post('/growth-stage/analyze');
      return response.data;
    } catch (error) {
      console.error('[growthStageService] Error analyzing growth stage:', error);
      throw new Error(error.response?.data?.error || 'Error al analizar etapa de crecimiento');
    }
  },

  /**
   * Obtener el análisis detallado más reciente
   */
  getDetailedAnalysis: async () => {
    try {
      const response = await api.get('/growth-stage/analysis');
      return response.data;
    } catch (error) {
      console.error('[growthStageService] Error getting detailed analysis:', error);
      throw new Error(error.response?.data?.error || 'Error al obtener análisis detallado');
    }
  },

  /**
   * Obtener historial de análisis de etapas
   */
  getAnalysisHistory: async () => {
    try {
      const response = await api.get('/growth-stage/history');
      return response.data;
    } catch (error) {
      console.error('[growthStageService] Error getting analysis history:', error);
      throw new Error(error.response?.data?.error || 'Error al obtener historial de análisis');
    }
  },

  /**
   * Obtener requisitos para la siguiente etapa
   */
  getNextStageRequirements: async () => {
    try {
      const response = await api.get('/growth-stage/next-requirements');
      return response.data;
    } catch (error) {
      console.error('[growthStageService] Error getting next stage requirements:', error);
      throw new Error(error.response?.data?.error || 'Error al obtener requisitos de siguiente etapa');
    }
  },

  /**
   * Función helper para formatear datos de etapa
   */
  formatStageData: (stageData) => {
    if (!stageData) return null;

    return {
      currentStage: stageData.currentStage,
      previousStage: stageData.previousStage,
      lastAnalysisDate: new Date(stageData.lastAnalysisDate).toLocaleDateString(),
      metrics: {
        growthScore: stageData.compositeMetrics?.growthScore || 0,
        marketScore: stageData.compositeMetrics?.marketScore || 0,
        financialScore: stageData.compositeMetrics?.financialScore || 0,
        performanceScore: stageData.compositeMetrics?.performanceScore || 0,
        overallScore: stageData.compositeMetrics?.overallScore || 0
      },
      progression: {
        progress: stageData.stageProgression?.progression || 0,
        timeTaken: stageData.stageProgression?.timeTaken || 0,
        growthRate: stageData.stageProgression?.growthRate || 0
      }
    };
  },

  /**
   * Función helper para formatear análisis detallado
   */
  formatDetailedAnalysis: (analysis) => {
    if (!analysis) return null;

    return {
      currentStage: analysis.currentStage,
      summary: analysis.analysisDetails?.summary || '',
      keyFactors: analysis.analysisDetails?.keyFactors || [],
      metrics: analysis.metrics || {},
      recommendations: {
        immediate: analysis.recommendations?.immediate || [],
        shortTerm: analysis.recommendations?.shortTerm || [],
        longTerm: analysis.recommendations?.longTerm || []
      },
      opportunities: analysis.opportunities || [],
      risks: analysis.risks || [],
      nextStage: {
        name: analysis.nextStage?.target || '',
        requirements: analysis.nextStage?.requirements || [],
        estimatedMonths: analysis.nextStage?.estimatedTimeframe || 0
      }
    };
  },

  /**
   * Función helper para manejar errores
   */
  handleError: (error) => {
    console.error('[growthStageService] Error:', error);
    
    if (error.response?.status === 401) {
      throw new Error('Sesión expirada. Por favor, vuelve a iniciar sesión.');
    }
    
    if (error.response?.status === 404) {
      throw new Error('No se encontró información de análisis.');
    }
    
    throw new Error(error.response?.data?.error || 'Error en el servicio de análisis de etapas');
  }
};

export default growthStageService;